import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { ContentfulClientApi, createClient } from 'contentful';

import { NibtravelAxiosAdapterService } from './axios.adapter.service';
import { CONTENTFUL_CONFIG } from './contentful.token';
import {
  ContentfulClientParams,
  ICreateClientParams,
} from './models/contentful.model';

/**
 * This is a safer implementation for the config.
 * For Angular Universal, the config is provided by the lambda handler.
 * However, this means the config won't be available by the browser AppModule.
 * Hence, this implementation uses the transfer state technique to ensure the
 * contentful config is available on browser so if somethings needs to be loaded by
 * the browser asynchronously, it can be done as the the config and contentful client
 * will be available at all times.
 */
@Injectable({ providedIn: 'root' })
export class ContentfulProvider {
  CONFIG_KEY = makeStateKey<ICreateClientParams>('CONTENTFUL_CONFIG_KEY');
  client!: ContentfulClientApi;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(CONTENTFUL_CONFIG) @Optional() private config: ICreateClientParams,
    private readonly ngAxiosAdapter: NibtravelAxiosAdapterService,
    private transferState: TransferState
  ) {
    try {
      const contentfulConfig = this.getConfig();
      if (contentfulConfig) {
        this.client = createClient(contentfulConfig);
      } else if (isPlatformServer(platformId)) {
        // Should only log this on Server Side as this should be a real problem
        console.log('Could not create contentful client');
      }
    } catch (error) {
      console.log(error);
    }
  }

  private getConfig(): ICreateClientParams | null {
    let contentfulConfig: ICreateClientParams | null;
    if (isPlatformServer(this.platformId)) {
      contentfulConfig = this.createAndCacheConfig();
    } else if (this.transferState.hasKey(this.CONFIG_KEY)) {
      contentfulConfig = this.transferState.get(this.CONFIG_KEY, null);
    } else if (this.config) {
      contentfulConfig = this.createAndCacheConfig();
    } else {
      contentfulConfig = null;
    }

    return contentfulConfig;
  }

  private createAndCacheConfig() {
    const contentfulConfig = ContentfulClientParams.create(
      this.config,
      this.ngAxiosAdapter.adapter
    );

    this.transferState.set(this.CONFIG_KEY, contentfulConfig);
    return contentfulConfig;
  }
}
